import { AlertTriangle, CheckCircle2, Receipt, RotateCcw } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { SavingsAccount, SavingsPosting } from '@/api/savings-types'
import type { DualControlResult } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useHeldVerification } from '@/features/customers/verification-store'
import { VerificationGate } from '@/features/customers/verify-customer-dialog'
import { formatShortDate } from '@/lib/datetime'
import type { Pesewas } from '@/lib/money'

import { DestinationAccountPicker } from './account-picker'
import { CashPreconditions } from './cash-preconditions'
import { transactionTypeLabel } from './constants'
import { SavingsDualControlNotice } from './dual-control'
import {
  usePostDeposit,
  usePostTransfer,
  usePostWithdrawal,
  useTopUpSecurityDeposit,
} from './mutations'
import { SAVINGS_PERMISSIONS, useSavingsPermissions } from './permissions'
import { SavingsProblemAlert } from './savings-errors'
import { useClientOperationId } from './use-client-operation-id'

/**
 * The four cash forms.
 *
 * They share a shape — amount, value date, narration, submit, read the receipt back — and differ
 * in three ways that matter more than the shape does.
 *
 * **What the response says.** A deposit, a transfer, and a security-deposit top-up answer with the
 * posting: transaction number, balance after, available after, and a `wasReplay` flag. A withdrawal
 * answers with a `DualControlResult` and nothing else — no transaction number, no balances — so its
 * receipt cannot be built and this screen does not pretend otherwise.
 *
 * **Which drawer permission applies.** Cash in needs the till's `teller.deposit.post`, cash out its
 * `teller.withdrawal.post`; a transfer needs neither, because it moves no notes.
 *
 * **What a mis-tap costs.** A deposit aimed at a security deposit account and a top-up aimed at a
 * regular savings one are the two likeliest wrong tabs, and both are refused with
 * `savings.account_wrong_class`. Each form therefore says which class of account it belongs on and
 * refuses to submit against the other one before the money is taken.
 */

const today = () => new Date().toISOString().slice(0, 10)

interface CashFormShellProps {
  account: SavingsAccount
  /** The operator has to be able to name what they are about to do. */
  title: string
  description: ReactNode
  /** Read before the fields — the till and business-day preconditions, dual-control notices. */
  notice?: ReactNode
  amount: Pesewas | null
  onAmountChange: (amount: Pesewas | null) => void
  valueDate: string
  onValueDateChange: (valueDate: string) => void
  narration: string
  onNarrationChange: (narration: string) => void
  /** Renders under the amount — a ceiling, a fee warning, a class mismatch. */
  amountHint?: ReactNode
  submitLabel: string
  isPending: boolean
  disabled: boolean
  error: Parameters<typeof SavingsProblemAlert>[0]['error'] | null
  onSubmit: () => void
  /** Present once the posting has settled; it replaces the form. */
  outcome?: ReactNode
  onStartAnother?: () => void
  /** The idempotency key that will accompany the next submission. */
  operationId: string
}

function CashFormShell({
  account,
  title,
  description,
  notice,
  amount,
  onAmountChange,
  valueDate,
  onValueDateChange,
  narration,
  onNarrationChange,
  amountHint,
  submitLabel,
  isPending,
  disabled,
  error,
  onSubmit,
  outcome,
  onStartAnother,
  operationId,
}: CashFormShellProps) {
  const fieldPrefix = `cash-${title.toLowerCase().replace(/\W+/g, '-')}`

  if (outcome) {
    return (
      <div className="space-y-3">
        {outcome}
        {onStartAnother ? (
          <Button variant="primary" onClick={onStartAnother}>
            <RotateCcw />
            Start another
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!disabled && !isPending) {
          onSubmit()
        }
      }}
    >
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-heading">{title}</h2>
        <p className="text-sm text-body">{description}</p>
      </div>

      {notice}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${fieldPrefix}-amount`}>Amount</Label>
          <MoneyInput
            id={`${fieldPrefix}-amount`}
            value={amount}
            onChange={onAmountChange}
            currency={account.currencyCode}
            autoFocus
          />
          {amountHint}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${fieldPrefix}-value-date`}>Value date (optional)</Label>
          <Input
            id={`${fieldPrefix}-value-date`}
            type="date"
            value={valueDate}
            max={today()}
            onChange={(event) => onValueDateChange(event.target.value)}
            aria-describedby={`${fieldPrefix}-value-date-help`}
          />
          <p id={`${fieldPrefix}-value-date-help`} className="text-xs text-muted">
            Left empty, the posting takes the branch&apos;s open business date. Backdating is
            permitted inside the institution&apos;s window and never into the future.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${fieldPrefix}-narration`}>Narration (optional)</Label>
        <Input
          id={`${fieldPrefix}-narration`}
          value={narration}
          autoComplete="off"
          // 500 on every movement validator — `savings.narration_too_long` otherwise.
          maxLength={500}
          onChange={(event) => onNarrationChange(event.target.value)}
        />
      </div>

      {error ? <SavingsProblemAlert error={error} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" disabled={disabled || isPending}>
          {isPending ? 'Posting…' : submitLabel}
        </Button>
        {/*
          The key is shown, small, because it is what a teller quotes when a posting times out and
          they need somebody to say whether it landed. It is regenerated after every real posting.
        */}
        <span className="font-mono text-2xs text-muted">op {operationId.slice(0, 8)}</span>
      </div>
    </form>
  )
}

/**
 * The receipt for a posting that answered with one.
 *
 * `wasReplay` is rendered as a different thing entirely, not as a footnote. It means the server
 * matched the operation key and returned an *earlier* posting: no second credit was made, and every
 * figure below describes that earlier transaction rather than what was just keyed.
 */
function PostingReceipt({
  posting,
  account,
  action,
}: {
  posting: SavingsPosting
  account: SavingsAccount
  action: string
}) {
  return (
    <Alert tone={posting.wasReplay ? 'warning' : 'success'}>
      {posting.wasReplay ? <AlertTriangle aria-hidden /> : <Receipt aria-hidden />}
      <div className="w-full min-w-0 space-y-2">
        <AlertTitle>
          {posting.wasReplay ? 'Already posted — nothing was taken twice' : `${action} posted`}
        </AlertTitle>
        <AlertDescription>
          {posting.wasReplay ? (
            <>
              This operation key had already been used, so the server returned the posting it made
              then and made no second one. Check the transaction number below against the passbook
              before taking any more money.
            </>
          ) : (
            <>Read the transaction number back to the customer and write it into the passbook.</>
          )}
        </AlertDescription>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Transaction</dt>
            <dd className="mt-0.5 font-mono text-sm text-heading">
              {posting.transaction.transactionNumber}
            </dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Type</dt>
            <dd className="mt-0.5 text-sm text-heading">
              {transactionTypeLabel(posting.transaction.type)}
            </dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Value date</dt>
            <dd className="numeric mt-0.5 text-sm text-heading tabular-nums">
              {formatShortDate(posting.transaction.valueDate)}
            </dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Amount</dt>
            <dd className="mt-0.5">
              <MoneyDisplay
                value={posting.transaction.amountMinor}
                currency={posting.transaction.currencyCode}
                emphasis="strong"
                className="text-left"
              />
            </dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Balance after</dt>
            <dd className="mt-0.5">
              <MoneyDisplay
                value={posting.balanceAfterMinor}
                currency={posting.currencyCode}
                className="text-left"
              />
            </dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Available after</dt>
            <dd className="mt-0.5">
              <MoneyDisplay
                value={posting.availableBalanceMinor}
                currency={posting.currencyCode}
                className="text-left"
              />
            </dd>
          </div>
        </dl>

        {posting.availableBalanceMinor !== posting.balanceAfterMinor ? (
          <p className="text-xs text-body">
            The available balance is lower than the ledger balance because{' '}
            <MoneyDisplay
              value={posting.balanceAfterMinor - posting.availableBalanceMinor}
              currency={posting.currencyCode}
              className="inline text-left"
            />{' '}
            is on hold. {account.isWithdrawable ? 'Only the available figure may be withdrawn.' : ''}
          </p>
        ) : null}
      </div>
    </Alert>
  )
}

/** Cash in, to a regular savings account. */
export function DepositForm({ account, branchId }: { account: SavingsAccount; branchId: string | null }) {
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [narration, setNarration] = useState('')
  const [posting, setPosting] = useState<SavingsPosting | null>(null)
  const operation = useClientOperationId()
  const deposit = usePostDeposit(account.id)
  const held = useHeldVerification(account.customerId)

  const wrongClass = account.productClass === 'SecurityDeposit'

  const reset = () => {
    setAmount(null)
    setValueDate('')
    setNarration('')
    setPosting(null)
    deposit.reset()
  }

  return (
    <CashFormShell
      account={account}
      title="Deposit"
      description="Cash taken in over the counter and credited to the account."
      notice={
        <div className="space-y-2">
          <CashPreconditions branchId={branchId} direction="in" />
          <VerificationGate
            customerId={account.customerId}
            customerName={account.customerName}
            required={false}
          />
          {wrongClass ? (
            <Alert tone="danger">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This is a security deposit account</AlertTitle>
                <AlertDescription>
                  A deposit posted here is refused with{' '}
                  <span className="font-mono text-xs">savings.account_wrong_class</span>. Cash
                  collateral goes in through the security deposit tab.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </div>
      }
      amount={amount}
      onAmountChange={setAmount}
      valueDate={valueDate}
      onValueDateChange={setValueDate}
      narration={narration}
      onNarrationChange={setNarration}
      submitLabel="Post deposit"
      isPending={deposit.isPending}
      disabled={amount === null || amount <= 0 || wrongClass}
      error={deposit.isError ? deposit.error : null}
      operationId={operation.current}
      outcome={
        posting ? <PostingReceipt posting={posting} account={account} action="Deposit" /> : null
      }
      onStartAnother={reset}
      onSubmit={() => {
        if (amount === null) {
          return
        }

        deposit.mutate(
          {
            amountMinor: amount,
            currencyCode: account.currencyCode,
            valueDate: valueDate === '' ? null : valueDate,
            narration: narration.trim() === '' ? null : narration.trim(),
            clientOperationId: operation.current,
            verificationId: held?.id ?? null,
          },
          {
            onSuccess: (result) => {
              setPosting(result)
              // Spent either way. A replay means the key was already consumed by an earlier
              // posting; a real credit means it has been consumed now. Carrying it forward is what
              // makes the next customer's deposit vanish into the last one's receipt.
              operation.spend()
            },
          },
        )
      }}
    />
  )
}

/**
 * Cash out. Dual-controlled, and the response carries no posting figures at all.
 */
export function WithdrawalForm({
  account,
  branchId,
}: {
  account: SavingsAccount
  branchId: string | null
}) {
  const permissions = useSavingsPermissions()
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [narration, setNarration] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)
  const [attempted, setAttempted] = useState<Pesewas | null>(null)
  const operation = useClientOperationId()
  const withdraw = usePostWithdrawal(account.id)
  const held = useHeldVerification(account.customerId)

  const notWithdrawable = !account.isWithdrawable
  const overAvailable = amount !== null && amount > account.availableBalanceMinor

  const reset = () => {
    setAmount(null)
    setValueDate('')
    setNarration('')
    setResult(null)
    setAttempted(null)
    withdraw.reset()
  }

  return (
    <CashFormShell
      account={account}
      title="Withdrawal"
      description="Savings paid out over the counter, against the available balance."
      notice={
        <div className="space-y-2">
          <CashPreconditions branchId={branchId} direction="out" />
          <VerificationGate
            customerId={account.customerId}
            customerName={account.customerName}
            required
          />
          <SavingsDualControlNotice
            action="Paying a withdrawal out"
            approvePermission={SAVINGS_PERMISSIONS.withdrawalPostApprove}
            canCountersign={permissions.canCountersignWithdrawal}
          >
            <AlertDescription>
              If it is queued, the cash leg runs against <strong>your</strong> till at the moment
              somebody countersigns it, not theirs. Close your session before that happens and the
              countersigned withdrawal fails with{' '}
              <span className="font-mono text-xs">teller.no_open_till</span> — and the failure
              surfaces in the approvals inbox, not here. Keep the drawer open until it clears, or
              tell the approver it is waiting.
            </AlertDescription>
          </SavingsDualControlNotice>
          {notWithdrawable ? (
            <Alert tone="danger">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This account cannot be withdrawn from</AlertTitle>
                <AlertDescription>
                  It is a security deposit — collateral under the group guarantee. It is released by
                  a countersigned guarantee offset, or by closing the account.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </div>
      }
      amount={amount}
      onAmountChange={setAmount}
      valueDate={valueDate}
      onValueDateChange={setValueDate}
      narration={narration}
      onNarrationChange={setNarration}
      amountHint={
        <div className="space-y-1">
          <p className="text-xs text-muted">
            Available{' '}
            <MoneyDisplay
              value={account.availableBalanceMinor}
              currency={account.currencyCode}
              className="inline text-left"
            />
            {account.holdAmountMinor > 0 ? ' — holds are already excluded.' : '.'}
          </p>
          <p className="text-xs text-muted">
            A product that charges a withdrawal fee posts it as a <strong>second</strong> statement
            line with its own transaction number. The customer&apos;s balance falls by more than the
            figure keyed here.
          </p>
          {overAvailable ? (
            <p className="text-xs text-danger-text">
              That is more than the available balance. The server refuses it with
              savings.insufficient_available_balance, and a product minimum balance can refuse a
              smaller figure still.
            </p>
          ) : null}
        </div>
      }
      submitLabel="Post withdrawal"
      isPending={withdraw.isPending}
      disabled={amount === null || amount <= 0 || notWithdrawable || overAvailable || !held}
      error={withdraw.isError ? withdraw.error : null}
      operationId={operation.current}
      outcome={
        result ? (
          <WithdrawalOutcome result={result} account={account} amount={attempted} />
        ) : null
      }
      onStartAnother={reset}
      onSubmit={() => {
        if (amount === null) {
          return
        }

        setAttempted(amount)
        withdraw.mutate(
          {
            amountMinor: amount,
            currencyCode: account.currencyCode,
            valueDate: valueDate === '' ? null : valueDate,
            narration: narration.trim() === '' ? null : narration.trim(),
            clientOperationId: operation.current,
            verificationId: held?.id ?? null,
          },
          {
            onSuccess: (answer) => {
              setResult(answer)
              // Spent whether it executed or queued: a queued withdrawal has this key recorded in
              // its stored approval payload, and reusing it would replay against that.
              operation.spend()
            },
          },
        )
      }}
    />
  )
}

/**
 * What a withdrawal can honestly report.
 *
 * An executed withdrawal answers `DualControlResult.Executed(entityId)` — awaiting flag, request
 * id, entity id, and nothing else. There is no transaction number on the wire, no balance after, no
 * available after, and no endpoint that reads one savings transaction. So this states the amount
 * that was *keyed*, says where the figures actually are, and sends the operator to the statement
 * rather than inventing a receipt.
 */
function WithdrawalOutcome({
  result,
  account,
  amount,
}: {
  result: DualControlResult
  account: SavingsAccount
  amount: Pesewas | null
}) {
  if (result.awaitingApproval) {
    return (
      <Alert tone="warning">
        <AlertTriangle aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Sent for approval — do not pay the money out</AlertTitle>
          <AlertDescription>
            {amount === null ? 'The withdrawal' : null}
            {amount === null ? null : (
              <MoneyDisplay
                value={amount}
                currency={account.currencyCode}
                className="inline text-left"
              />
            )}{' '}
            against {account.accountNumber} is stored and waiting for a countersignature. Nothing has
            been debited and no cash has left the drawer.
            {result.approvalRequestId ? (
              <>
                {' '}
                Request <span className="font-mono text-xs">{result.approvalRequestId}</span>.
              </>
            ) : null}{' '}
            When it is countersigned the cash leg runs against your till, so keep the session open
            until it clears.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <Alert tone="success">
      <CheckCircle2 aria-hidden />
      <div className="space-y-1">
        <AlertTitle>Withdrawal posted</AlertTitle>
        <AlertDescription>
          {amount === null ? null : (
            <>
              <MoneyDisplay
                value={amount}
                currency={account.currencyCode}
                className="inline text-left"
              />{' '}
            </>
          )}
          has been debited from {account.accountNumber}.{' '}
          <strong>
            The server returns no transaction number, balance, or available balance for a withdrawal
          </strong>{' '}
          — those figures exist only on the statement. Open the statement to read the transaction
          number into the passbook, and expect a separate withdrawal-fee line beside it if the
          product charges one.
        </AlertDescription>
      </div>
    </Alert>
  )
}

/** Book-to-book. No notes move, so no till and no drawer permission. */
export function TransferForm({
  account,
  branchId,
}: {
  account: SavingsAccount
  branchId: string | null
}) {
  const [destination, setDestination] = useState<SavingsAccount | null>(null)
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [narration, setNarration] = useState('')
  const [posting, setPosting] = useState<SavingsPosting | null>(null)
  const operation = useClientOperationId()
  const transfer = usePostTransfer(account.id)
  const held = useHeldVerification(account.customerId)

  const overAvailable = amount !== null && amount > account.availableBalanceMinor
  const currencyMismatch = destination !== null && destination.currencyCode !== account.currencyCode
  // A transfer debits the source exactly as a withdrawal does, and the server refuses a
  // non-withdrawable source with the same `savings.account_not_withdrawable`. Guarding the
  // withdrawal tab and not this one would leave the transfer tab as an apparent way to move
  // collateral out of a security deposit.
  const notWithdrawable = !account.isWithdrawable

  const reset = () => {
    setDestination(null)
    setAmount(null)
    setValueDate('')
    setNarration('')
    setPosting(null)
    transfer.reset()
  }

  return (
    <CashFormShell
      account={account}
      title="Transfer"
      description="Money moved between two customer accounts. Nothing passes through a drawer."
      notice={
        <div className="space-y-2">
          <CashPreconditions branchId={branchId} needsTill={false} />
          <VerificationGate
            customerId={account.customerId}
            customerName={account.customerName}
            required
          />
          {notWithdrawable ? (
            <Alert tone="danger">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>Nothing can be transferred out of this account</AlertTitle>
                <AlertDescription>
                  It is a security deposit — collateral under the group guarantee. A transfer debits
                  it exactly as a withdrawal does, and the server refuses it with{' '}
                  <span className="font-mono text-xs">savings.account_not_withdrawable</span>. The
                  money leaves by a countersigned guarantee offset, or by closing the account.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
          <DestinationAccountPicker
            sourceAccountId={account.id}
            currency={account.currencyCode}
            selected={destination}
            onSelect={setDestination}
          />
        </div>
      }
      amount={amount}
      onAmountChange={setAmount}
      valueDate={valueDate}
      onValueDateChange={setValueDate}
      narration={narration}
      onNarrationChange={setNarration}
      amountHint={
        <div className="space-y-1">
          <p className="text-xs text-muted">
            Available{' '}
            <MoneyDisplay
              value={account.availableBalanceMinor}
              currency={account.currencyCode}
              className="inline text-left"
            />
            .
          </p>
          {overAvailable ? (
            <p className="text-xs text-danger-text">
              That is more than the available balance.
            </p>
          ) : null}
        </div>
      }
      submitLabel="Post transfer"
      isPending={transfer.isPending}
      disabled={
        amount === null ||
        amount <= 0 ||
        destination === null ||
        overAvailable ||
        currencyMismatch ||
        notWithdrawable ||
        !held
      }
      error={transfer.isError ? transfer.error : null}
      operationId={operation.current}
      outcome={
        posting ? (
          <div className="space-y-2">
            <PostingReceipt posting={posting} account={account} action="Transfer" />
            <p className="text-xs text-muted">
              The figures above describe this account only. The destination&apos;s balance is not on
              this response and has not been guessed at — open it to read it.
            </p>
          </div>
        ) : null
      }
      onStartAnother={reset}
      onSubmit={() => {
        if (amount === null || destination === null) {
          return
        }

        transfer.mutate(
          {
            destinationAccountId: destination.id,
            amountMinor: amount,
            currencyCode: account.currencyCode,
            valueDate: valueDate === '' ? null : valueDate,
            narration: narration.trim() === '' ? null : narration.trim(),
            clientOperationId: operation.current,
            verificationId: held?.id ?? null,
          },
          {
            onSuccess: (result) => {
              setPosting(result)
              operation.spend()
            },
          },
        )
      }}
    />
  )
}

/** Cash collateral in, to a security deposit account. */
export function SecurityDepositForm({
  account,
  branchId,
}: {
  account: SavingsAccount
  branchId: string | null
}) {
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [valueDate, setValueDate] = useState('')
  const [narration, setNarration] = useState('')
  const [posting, setPosting] = useState<SavingsPosting | null>(null)
  const operation = useClientOperationId()
  const topUp = useTopUpSecurityDeposit(account.id)

  const wrongClass = account.productClass !== 'SecurityDeposit'

  const reset = () => {
    setAmount(null)
    setValueDate('')
    setNarration('')
    setPosting(null)
    topUp.reset()
  }

  return (
    <CashFormShell
      account={account}
      title="Security deposit"
      description="Cash collateral taken in against the group guarantee. It is not the customer's spendable balance."
      notice={
        <div className="space-y-2">
          <CashPreconditions branchId={branchId} direction="in" />
          {wrongClass ? (
            <Alert tone="danger">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This is a regular savings account</AlertTitle>
                <AlertDescription>
                  A security-deposit top-up posted here is refused with{' '}
                  <span className="font-mono text-xs">savings.account_wrong_class</span>. Ordinary
                  cash in goes through the deposit tab; collateral belongs on the customer&apos;s
                  security deposit account.
                </AlertDescription>
              </div>
            </Alert>
          ) : (
            <Alert tone="info">
              <AlertTriangle aria-hidden />
              <div className="space-y-1">
                <AlertTitle>This money is not withdrawable</AlertTitle>
                <AlertDescription>
                  Once it is in, it leaves only by a countersigned guarantee offset or by closing the
                  account. Say so to the customer before taking it.
                </AlertDescription>
              </div>
            </Alert>
          )}
        </div>
      }
      amount={amount}
      onAmountChange={setAmount}
      valueDate={valueDate}
      onValueDateChange={setValueDate}
      narration={narration}
      onNarrationChange={setNarration}
      submitLabel="Post security deposit"
      isPending={topUp.isPending}
      disabled={amount === null || amount <= 0 || wrongClass}
      error={topUp.isError ? topUp.error : null}
      operationId={operation.current}
      outcome={
        posting ? (
          <PostingReceipt posting={posting} account={account} action="Security deposit" />
        ) : null
      }
      onStartAnother={reset}
      onSubmit={() => {
        if (amount === null) {
          return
        }

        topUp.mutate(
          {
            amountMinor: amount,
            currencyCode: account.currencyCode,
            valueDate: valueDate === '' ? null : valueDate,
            narration: narration.trim() === '' ? null : narration.trim(),
            clientOperationId: operation.current,
            // Collateral, not a customer-facing movement this fix's verification gate covers —
            // see the note atop `cash-forms.tsx`'s security deposit form.
            verificationId: null,
          },
          {
            onSuccess: (result) => {
              setPosting(result)
              operation.spend()
            },
          },
        )
      }}
    />
  )
}
